import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../../../hooks/useLang';
import { getLocalizedExperts, LocalizedExpert } from '../../../mocks/expertsLocalized';
import ExpertConsultModal from './ExpertConsultModal';

const CATEGORY_TABS = [
  { id: 'academic', icon: 'ri-graduation-cap-line', color: '#7C3AED' },
  { id: 'industry', icon: 'ri-building-4-line', color: '#DC2626' },
  { id: 'regulatory', icon: 'ri-shield-star-line', color: '#059669' },
  { id: 'international', icon: 'ri-global-line', color: '#0EA5E9' },
];

const CATEGORY_LABELS: Record<string, Record<string, string>> = {
  academic: { zh: '学术先驱', en: 'Academic Pioneers', fr: 'Pionniers Académiques', es: 'Pioneros Académicos', pt: 'Pioneiros Acadêmicos', ja: '学術先駆者', de: 'Akademische Pioniere', ko: '학술 선구자', ar: 'رواد أكاديميون', ru: 'Академические Пионеры' },
  industry: { zh: '产业领袖', en: 'Industry Leaders', fr: 'Leaders Industriels', es: 'Líderes Industriales', pt: 'Líderes da Indústria', ja: '産業リーダー', de: 'Branchenführer', ko: '산업 리더', ar: 'قادة الصناعة', ru: 'Лидеры Отрасли' },
  regulatory: { zh: '监管先锋', en: 'Regulatory Pioneers', fr: 'Pionniers Réglementaires', es: 'Pioneros Regulatorios', pt: 'Pioneiros Regulatórios', ja: '規制先駆者', de: 'Regulierungspioniere', ko: '규제 선구자', ar: 'رواد تنظيميون', ru: 'Регуляторные Пионеры' },
  international: { zh: '国际人物', en: 'International Figures', fr: 'Figures Internationales', es: 'Figuras Internacionales', pt: 'Figuras Internacionais', ja: '国際的人物', de: 'Internationale Persönlichkeiten', ko: '국제 인물', ar: 'شخصيات دولية', ru: 'Международные Фигуры' },
};

const SECTION_LABELS: Record<string, Record<string, string>> = {
  sectionBadge: { zh: '行业人物志', en: 'Industry Profiles', fr: 'Profils Industriels', es: 'Perfiles Industriales', pt: 'Perfis da Indústria', ja: '業界人物誌', de: 'Branchenprofile', ko: '업계 인물지', ar: 'ملفات الصناعة', ru: 'Профили Отрасли' },
  title1: { zh: '区块链与RWA领域', en: 'Blockchain & RWA ', fr: 'Blockchain & RWA ', es: 'Blockchain & RWA ', pt: 'Blockchain & RWA ', ja: 'ブロックチェーン・RWA分野の', de: 'Blockchain & RWA ', ko: '블록체인 & RWA 분야', ar: 'مجال البلوكتشين وRWA ', ru: 'Блокчейн и RWA ' },
  title2: { zh: '权威人物', en: 'Authority Figures', fr: 'Figures Clés', es: 'Figuras Clave', pt: 'Figuras de Autoridade', ja: '権威ある人物', de: 'Schlüsselfiguren', ko: '권위 있는 인물', ar: 'شخصيات رائدة', ru: 'Ключевые Фигуры' },
  subtitle: { zh: '他们是科学家、企业家、监管者与布道者——每一位都在用不同方式，推动真实世界资产与区块链的深度融合。', en: 'Scientists, entrepreneurs, regulators, and visionaries — each shaping the fusion of real-world assets and blockchain in their own way.', fr: 'Scientifiques, entrepreneurs, régulateurs et visionnaires — chacun façonnant la fusion des actifs réels et de la blockchain à sa manière.', es: 'Científicos, empresarios, reguladores y visionarios — cada uno dando forma a la fusión de activos reales y blockchain a su manera.', pt: 'Cientistas, empreendedores, reguladores e visionários — cada um moldando a fusão de ativos reais e blockchain à sua maneira.', ja: '科学者、起業家、規制当局、ビジョナリー——それぞれが独自の方法で、実物資産とブロックチェーンの融合を形作っています。', de: 'Wissenschaftler, Unternehmer, Regulatoren und Visionäre — jeder gestaltet die Fusion von realen Vermögenswerten und Blockchain auf seine eigene Weise.', ko: '과학자, 기업가, 규제 당국, 비전가 — 각자의 방식으로 실물 자산과 블록체인의 융합을 형성하고 있습니다.', ar: 'علماء ورجال أعمال ومنظمون وأصحاب رؤية — كل منهم يشكّل دمج الأصول الحقيقية مع البلوكتشين بطريقته الخاصة.', ru: 'Учёные, предприниматели, регуляторы и визионеры — каждый формирует слияние реальных активов и блокчейна по-своему.' },
  learnMore: { zh: '深入了解', en: 'Learn More', fr: 'En Savoir Plus', es: 'Saber Más', pt: 'Saiba Mais', ja: '詳細を見る', de: 'Mehr Erfahren', ko: '자세히 알아보기', ar: 'اعرف المزيد', ru: 'Узнать Больше' },
  rwaContrib: { zh: 'RWA贡献', en: 'RWA Contribution', fr: 'Contribution RWA', es: 'Contribución RWA', pt: 'Contribuição RWA', ja: 'RWA貢献', de: 'RWA-Beitrag', ko: 'RWA 기여', ar: 'مساهمة RWA', ru: 'Вклад в RWA' },
  localFirst: { zh: '本地优先', en: 'Local First', fr: 'Local en Premier', es: 'Local Primero', pt: 'Local Primeiro', ja: 'ローカル優先', de: 'Lokal Zuerst', ko: '로컬 우선', ar: 'المحلي أولاً', ru: 'Местные Сначала' },
};

function getLabel(key: string, lang: string): string {
  return SECTION_LABELS[key]?.[lang] ?? SECTION_LABELS[key]?.['en'] ?? key;
}

function getCatLabel(catId: string, lang: string): string {
  return CATEGORY_LABELS[catId]?.[lang] ?? CATEGORY_LABELS[catId]?.['en'] ?? catId;
}

export default function ExpertProfiles() {
  const [activeTab, setActiveTab] = useState<string>('academic');
  const [consultExpert, setConsultExpert] = useState<LocalizedExpert | null>(null);
  const { lang, t } = useLang();
  const navigate = useNavigate();

  const allExperts = getLocalizedExperts(lang);
  const availableCats = CATEGORY_TABS.filter(cat => allExperts.some(e => e.category === cat.id));
  const filtered = allExperts.filter(e => e.category === activeTab);
  const activeCat = CATEGORY_TABS.find(c => c.id === activeTab) ?? CATEGORY_TABS[0];

  // If current tab has no experts, switch to first available
  const displayExperts = filtered.length > 0 ? filtered : allExperts.slice(0, 6);

  return (
    <section id="experts" className="py-20 bg-white dark:bg-gray-900 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-[0.03]" style={{ background: 'radial-gradient(circle, #7C3AED, transparent)' }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-[0.03]" style={{ background: 'radial-gradient(circle, #F59E0B, transparent)' }} />
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4" style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.15)' }}>
            <i className="ri-user-star-line text-[#7C3AED] text-sm"></i>
            <span className="text-[#7C3AED] text-sm font-semibold">{getLabel('sectionBadge', lang)}</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-black text-gray-900 dark:text-white mb-4">
            {getLabel('title1', lang)}
            <span className="text-[#7C3AED]">{getLabel('title2', lang)}</span>
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto text-base leading-relaxed">
            {getLabel('subtitle', lang)}
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex bg-gray-100 dark:bg-gray-800 rounded-full p-1 gap-1 flex-wrap justify-center">
            {availableCats.map((cat) => {
              const count = allExperts.filter(e => e.category === cat.id).length;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(cat.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                    activeTab === cat.id ? 'text-white shadow-md' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                  style={activeTab === cat.id ? { background: cat.color } : {}}
                >
                  <i className={cat.icon}></i>
                  {getCatLabel(cat.id, lang)}
                  <span className="text-xs opacity-80">({count})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Expert Cards Grid */}
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {displayExperts.map((expert) => (
            <ExpertCard
              key={expert.id}
              expert={expert}
              lang={lang}
              learnMoreLabel={getLabel('learnMore', lang)}
              rwaContribLabel={getLabel('rwaContrib', lang)}
              onConsult={() => setConsultExpert(expert)}
            />
          ))}
        </div>

      {/* Consult Modal */}
      {consultExpert && (
        <ExpertConsultModal expert={consultExpert} onClose={() => setConsultExpert(null)} />
      )}

        {/* Bottom Stats */}
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {availableCats.map((cat) => {
            const count = allExperts.filter(e => e.category === cat.id).length;
            return (
              <div key={cat.id} className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 rounded-2xl px-5 py-4 border border-gray-100 dark:border-gray-700">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0" style={{ background: `${cat.color}15` }}>
                  <i className={`${cat.icon} text-base`} style={{ color: cat.color }}></i>
                </div>
                <div>
                  <div className="text-2xl font-black text-gray-900 dark:text-white leading-none">{count}+</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{getCatLabel(cat.id, lang)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

interface ExpertCardProps {
  expert: LocalizedExpert;
  lang: string;
  learnMoreLabel: string;
  rwaContribLabel: string;
  onConsult: () => void;
}

function ExpertCard({ expert, lang, learnMoreLabel, rwaContribLabel, onConsult }: ExpertCardProps) {
  return (
    <div
      className="group bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl overflow-hidden hover:border-gray-200 dark:hover:border-gray-600 hover:shadow-lg transition-all duration-300 cursor-pointer"
    >
      <div className="h-1" style={{ background: `linear-gradient(90deg, ${expert.color}, ${expert.color}88)` }} />
      <div className="p-6">
        {/* Avatar & Name Row */}
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl shrink-0 text-white text-2xl" style={{ background: `linear-gradient(135deg, ${expert.color}, ${expert.color}99)` }}>
            <i className={expert.icon}></i>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <h3 className="font-black text-gray-900 dark:text-white text-lg leading-tight">{expert.name}</h3>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium text-white" style={{ background: expert.color }}>
                {expert.nationality}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed line-clamp-2">{expert.title}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{expert.affiliation}</p>
          </div>
        </div>

        {/* Headline */}
        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 line-clamp-2 font-medium">{expert.headline}</p>

        {/* RWA Contribution */}
        <div className="rounded-xl p-3 mb-4" style={{ background: `${expert.color}08`, border: `1px solid ${expert.color}20` }}>
          <div className="flex items-center gap-2 mb-1.5">
            <i className="ri-award-line text-xs" style={{ color: expert.color }}></i>
            <span className="text-xs font-bold" style={{ color: expert.color }}>{rwaContribLabel}</span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2">
            {expert.rwaContribution}
          </p>
        </div>

        {/* CTA */}
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onConsult(); }}
            className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap text-white"
            style={{ background: expert.color }}
          >
            <i className="ri-calendar-check-line"></i>
            预约咨询
          </button>
          <button
            className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition-all group-hover:opacity-90 cursor-pointer whitespace-nowrap"
            style={{ background: `${expert.color}15`, color: expert.color }}
          >
            {learnMoreLabel}
            <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
